// Multi-paragraph word-by-word transcript highlighting.
//
// HTML shape:
//   <block-transcribe-chat data-json='[{"word":"Hey","start":0.58,"end":0.94}, ...]'>
//     <div class="row" ...>
//       <p transcribe-text data-audio-start="0" data-audio-end="5.88"></p>
//     </div>
//     <div class="row" ...>
//       <p transcribe-text data-audio-start="5.88" data-audio-end="12.40"></p>
//     </div>
//     ...
//     <audio transcribe-audio src="..."></audio>  (or inside <core-audio>)
//   </block-transcribe-chat>
//
// Each [transcribe-text] target receives the slice of words whose start time
// falls within [data-audio-start, data-audio-end). Words are rendered as
// <span class="transcribe-word"> and .is-active is toggled on the span that
// matches the audio's currentTime.
//
// A word may carry "nbsp": true, which makes the space AFTER it non-breaking,
// so it stays on the same line as the next word (e.g. "lower back pain", "I sent").

export default class BlockTranscribeChat extends HTMLElement {
    connectedCallback() {
        let words = [];
        try {
            words = JSON.parse(this.getAttribute('data-json') || '[]');
        } catch (e) {
            return;
        }
        if (!Array.isArray(words) || !words.length) return;

        this.$audio = this.querySelector('audio[transcribe-audio]')
            || this.querySelector('[transcribe-audio] audio')
            || this.querySelector('audio');
        if (!this.$audio) return;

        const targets = Array.from(this.querySelectorAll('[transcribe-text]'));
        if (!targets.length) return;

        this.allSpans = [];
        this.allWords = [];

        targets.forEach((target) => {
            const start = parseFloat(target.dataset.audioStart ?? '0');
            const end = parseFloat(target.dataset.audioEnd ?? 'Infinity');
            const slice = words.filter((w) => w.start >= start && w.start < end);

            target.textContent = '';
            slice.forEach((w, i) => {
                const span = document.createElement('span');
                span.classList.add('transcribe-word');
                span.textContent = w.word;
                target.appendChild(span);
                if (i < slice.length - 1) {
                    // "nbsp": true keeps this word attached to the next one (non-breaking space).
                    target.appendChild(document.createTextNode(w.nbsp ? ' ' : ' '));
                }
                this.allSpans.push(span);
                this.allWords.push(w);
            });
        });

        this.activeIndex = -1;
        this.rafId = null;
        this.tick = this.tick.bind(this);
        this.onAudioPlay = () => this.startTicking();
        this.onAudioStop = () => {
            this.stopTicking();
            // run one final update so the last active state matches paused position
            this.updateHighlight();
        };

        this.$audio.addEventListener('play', this.onAudioPlay);
        this.$audio.addEventListener('pause', this.onAudioStop);
        this.$audio.addEventListener('ended', this.onAudioStop);
        this.$audio.addEventListener('seeked', () => this.updateHighlight());

        this.updateHighlight();
    }

    startTicking() {
        if (this.rafId !== null) return;
        this.rafId = requestAnimationFrame(this.tick);
    }

    stopTicking() {
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
        this.rafId = null;
    }

    tick() {
        this.updateHighlight();
        this.rafId = requestAnimationFrame(this.tick);
    }

    updateHighlight() {
        const t = this.$audio?.currentTime ?? 0;
        let idx = -1;
        for (let i = 0; i < this.allWords.length; i++) {
            const w = this.allWords[i];
            if (t >= w.start && t < w.end) {
                idx = i;
                break;
            }
        }
        if (idx === this.activeIndex) return;
        if (this.activeIndex >= 0) this.allSpans[this.activeIndex]?.classList.remove('is-active');
        if (idx >= 0) this.allSpans[idx]?.classList.add('is-active');
        this.activeIndex = idx;
    }

    disconnectedCallback() {
        this.stopTicking();
    }
}

customElements.define('block-transcribe-chat', BlockTranscribeChat);
